import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ExampleModal = ({ isOpen, onClose, title, examples }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            {examples?.map((example, index) => (
              <div key={index} className="p-4 bg-muted rounded-lg">
                {typeof example === 'string' ? (
                  <p className="text-sm text-foreground">{example}</p>
                ) : (
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">{example?.title}</h4>
                    <p className="text-sm text-muted-foreground">{example?.text}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 border-t border-border flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

const RequirementsSection = ({ data, onChange, errors }) => {
  const [newObjective, setNewObjective] = useState('');
  const [newConstraint, setNewConstraint] = useState('');  
  const [newDeliverable, setNewDeliverable] = useState('');
  const [newSuccessMetric, setNewSuccessMetric] = useState('');
  const [showObjectivesExample, setShowObjectivesExample] = useState(false);
  const [showConstraintsExample, setShowConstraintsExample] = useState(false);

  const objectiveExamples = [
    {
      title: 'Traffic Optimization',
      text: 'Reduce average peak-hour commute times by at least 15% within 6 months through data-driven traffic signal optimization and real-time congestion monitoring.'
    },
    {
      title: 'Service Delivery Improvement',
      text: 'Improve 311 request resolution time by 30% and provide residents with transparent, real-time status updates for all submitted requests.'
    },
    {
      title: 'Energy Cost Reduction',
      text: 'Decrease municipal building energy costs by 20% annually by implementing smart energy monitoring and usage analytics.'
    },
  ];

  const constraintExamples = [
    {
      title: 'Budget Constraint',
      text: 'The solution must be developed and deployed within a fixed budget of $250,000, including software, hardware, and training costs.'
    },
    {
      title: 'Data & Privacy Constraint',
      text: 'All resident and sensor data must comply with local privacy laws and cannot include personally identifiable information without explicit consent.'
    },
    {
      title: 'Capacity & Infrastructure Constraint',
      text: 'The solution must integrate with existing city IT systems and be usable by non-technical staff without requiring additional full-time hires.'
    },
  ];

  const deliverableTypes = [
    { id: 'problem-brief', label: 'Problem Brief Document', required: true },
    { id: 'prototype', label: 'Working Prototype', required: false },
    { id: 'pitch-deck', label: 'Pitch Deck Presentation', required: true },
    { id: 'mvp', label: 'Minimum Viable Product', required: false },
    { id: 'technical-docs', label: 'Technical Documentation', required: false },
    { id: 'user-testing', label: 'User Testing Results', required: false },
    { id: 'implementation-plan', label: 'Implementation Plan', required: false }
  ];

  const successMetrics = [
    'User adoption rate',
    'Cost reduction percentage',
    'Time savings achieved',
    'Process efficiency improvement',
    'User satisfaction score',
    'Environmental impact reduction',
    'Accessibility improvements',
    'Data accuracy enhancement'
  ];

  const addObjective = () => {
    if (newObjective?.trim()) {
      const objectives = data?.objectives || [];
      onChange('objectives', [...objectives, newObjective?.trim()]);
      setNewObjective('');
    }
  };

  const removeObjective = (index) => {
    const objectives = data?.objectives || [];
    onChange('objectives', objectives?.filter((_, i) => i !== index));
  };

  const addConstraint = () => {
    if (newConstraint?.trim()) {
      const constraints = data?.constraints || [];
      onChange('constraints', [...constraints, newConstraint?.trim()]);
      setNewConstraint('');
    }
  };

  const removeConstraint = (index) => {
    const constraints = data?.constraints || [];
    onChange('constraints', constraints?.filter((_, i) => i !== index));
  };

  const addCustomDeliverable = () => {
    if (newDeliverable?.trim()) {
      const deliverables = data?.expectedDeliverables || [];
      const customId = `custom-${Date.now()}`;
      onChange('expectedDeliverables', [...deliverables, { id: customId, label: newDeliverable?.trim(), custom: true }]);
      setNewDeliverable('');
    }
  };

  const addCustomSuccessMetric = () => {
    if (newSuccessMetric?.trim()) {
      const metrics = data?.successMetrics || [];
      onChange('successMetrics', [...metrics, newSuccessMetric?.trim()]);
      setNewSuccessMetric('');
    }
  };

  const handleDeliverableChange = (deliverableId, checked) => {
    const deliverables = data?.expectedDeliverables || [];
    if (checked) {
      onChange('expectedDeliverables', [...deliverables, deliverableId]);
    } else {
      // Handle both string IDs and custom objects
      onChange('expectedDeliverables', deliverables?.filter(d => {
        if (typeof d === 'string') return d !== deliverableId;
        return d?.id !== deliverableId;
      }));
    }
  };

  const removeCustomDeliverable = (deliverableId) => {
    const deliverables = data?.expectedDeliverables || [];
    onChange('expectedDeliverables', deliverables?.filter(d => {
      if (typeof d === 'string') return true;
      return d?.id !== deliverableId;
    }));
  };

  const handleSuccessMetricChange = (metric, checked) => {
    const metrics = data?.successMetrics || [];
    if (checked) {
      onChange('successMetrics', [...metrics, metric]);
    } else {
      onChange('successMetrics', metrics?.filter(m => m !== metric));
    }
  };

  const removeCustomSuccessMetric = (metric) => {
    const metrics = data?.successMetrics || [];
    onChange('successMetrics', metrics?.filter(m => m !== metric));
  };

  // Check if a deliverable is selected (handle both string and object format)
  const isDeliverableSelected = (deliverableId) => {
    const deliverables = data?.expectedDeliverables || [];
    return deliverables?.some(d => {
      if (typeof d === 'string') return d === deliverableId;
      return d?.id === deliverableId || d === deliverableId;
    });
  };

  // Get custom deliverables
  const customDeliverables = (data?.expectedDeliverables || [])?.filter(d => typeof d === 'object' && d?.custom);

  // Get custom success metrics (those not in predefined list)
  const customSuccessMetrics = (data?.successMetrics || [])?.filter(m => !successMetrics?.includes(m));

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="Target" size={16} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Requirements & Objectives</h2>
          <p className="text-sm text-muted-foreground">Define what success looks like</p>
        </div>
      </div>
      {/* Project Objectives */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Project Objectives</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowObjectivesExample(true)}
            iconName="HelpCircle"
            iconPosition="left"
            iconSize={16}
            className="text-primary hover:text-primary/80"
          >
            See Example
          </Button>
        </div>
        
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="Add a specific, measurable objective..."
            value={newObjective}
            onChange={(e) => setNewObjective(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Enter' && addObjective()}
            className="flex-1"
          />
          <Button
            variant="outline"
            onClick={addObjective}
            disabled={!newObjective?.trim()}
            iconName="Plus"
            iconSize={16}
          >
            Add
          </Button>
        </div>

        {data?.objectives && data?.objectives?.length > 0 && (
          <div className="space-y-2">
            {data?.objectives?.map((objective, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground flex-1">{objective}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeObjective(index)}
                  iconName="X"
                  iconSize={14}
                  className="h-6 w-6 text-muted-foreground hover:text-error"
                />
              </div>
            ))}
          </div>
        )}
        {errors?.objectives && (
          <p className="text-sm text-error">{errors?.objectives}</p>
        )}
      </div>
      {/* Constraints */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-foreground">Constraints & Limitations</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowConstraintsExample(true)}
            iconName="HelpCircle"
            iconPosition="left"
            iconSize={16}
            className="text-primary hover:text-primary/80"
          >
            See Example
          </Button>
        </div>
        
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="Add a constraint or limitation..."
            value={newConstraint}
            onChange={(e) => setNewConstraint(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Enter' && addConstraint()}
            className="flex-1"
          />
          <Button
            variant="outline"
            onClick={addConstraint}
            disabled={!newConstraint?.trim()}
            iconName="Plus"
            iconSize={16}
          >
            Add
          </Button>
        </div>

        {data?.constraints && data?.constraints?.length > 0 && (
          <div className="space-y-2">
            {data?.constraints?.map((constraint, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground flex-1">{constraint}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeConstraint(index)}
                  iconName="X"
                  iconSize={14}
                  className="h-6 w-6 text-muted-foreground hover:text-error"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Expected Deliverables */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Expected Deliverables</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {deliverableTypes?.map((deliverable) => (
            <Checkbox
              key={deliverable?.id}
              label={deliverable?.label}
              checked={isDeliverableSelected(deliverable?.id)}
              onChange={(e) => handleDeliverableChange(deliverable?.id, e?.target?.checked)}
              description={deliverable?.required ? 'Required' : 'Optional'}
            />
          ))}
        </div>

        {/* Custom Deliverables Input */}
        <div className="space-y-3">
          <div className="flex space-x-2">
            <Input
              type="text"
              placeholder="Add custom deliverable..."
              value={newDeliverable}
              onChange={(e) => setNewDeliverable(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && addCustomDeliverable()}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={addCustomDeliverable}
              disabled={!newDeliverable?.trim()}
              iconName="Plus"
              iconSize={16}
            >
              Add Custom
            </Button>
          </div>

          {customDeliverables?.length > 0 && (
            <div className="space-y-2">
              {customDeliverables?.map((deliverable) => (
                <div key={deliverable?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-foreground flex-1">{deliverable?.label}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCustomDeliverable(deliverable?.id)}
                    iconName="X"
                    iconSize={14}
                    className="h-6 w-6 text-muted-foreground hover:text-error"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {errors?.expectedDeliverables && (
          <p className="text-sm text-error">{errors?.expectedDeliverables}</p>
        )}
      </div>
      {/* Success Metrics */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">Success Metrics</h3>
        <p className="text-sm text-muted-foreground">
          Select metrics that will be used to measure project success
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {successMetrics?.map((metric) => (
            <Checkbox
              key={metric}
              label={metric}
              checked={(data?.successMetrics || [])?.includes(metric)}
              onChange={(e) => handleSuccessMetricChange(metric, e?.target?.checked)}
            />
          ))}
        </div>

        {/* Custom Success Metrics Input */}
        <div className="space-y-3">
          <div className="flex space-x-2">
            <Input
              type="text"
              placeholder="Add custom success metric..."
              value={newSuccessMetric}
              onChange={(e) => setNewSuccessMetric(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && addCustomSuccessMetric()}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={addCustomSuccessMetric}
              disabled={!newSuccessMetric?.trim()}
              iconName="Plus"
              iconSize={16}
            >
              Add Custom
            </Button>
          </div>

          {customSuccessMetrics?.length > 0 && (
            <div className="space-y-2">
              {customSuccessMetrics?.map((metric, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-foreground flex-1">{metric}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCustomSuccessMetric(metric)}
                    iconName="X"
                    iconSize={14}
                    className="h-6 w-6 text-muted-foreground hover:text-error"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Budget Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Resource Availability"
          type="text"
          placeholder="e.g., City data access, testing environment"
          value={data?.resourceAvailability || ''}
          onChange={(e) => onChange('resourceAvailability', e?.target?.value)}
          description="What resources can the city provide?"
        />
      </div>

      {/* Example Modals */}
      <ExampleModal
        isOpen={showObjectivesExample}
        onClose={() => setShowObjectivesExample(false)}
        title="Project Objectives Examples"
        examples={objectiveExamples}
      />
      <ExampleModal
        isOpen={showConstraintsExample}
        onClose={() => setShowConstraintsExample(false)}
        title="Constraints & Limitations Examples"
        examples={constraintExamples}
      />
    </div>
  );
};

export default RequirementsSection;